// src/components/tabs/STD213Tab.jsx
// California Standard Agreement (STD 213) — intelligent form with Penny conflict detection
// Auto-populates from Profile, STD 215, Contract, and Stage tab data
// Uses html2canvas + jsPDF to export (same as STD215Tab)

import { useState, useRef, useEffect } from 'react';

const PAGE_W = 780;

// ── Utilities ─────────────────────────────────────────────────────────────────
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
}

function numberToWords(amount) {
  if (!amount) return '';
  const str = String(amount).replace(/[$,\s]/g, '');
  const [dollars, cents] = str.split('.');
  const num = parseInt(dollars, 10);
  if (isNaN(num)) return '';
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
    'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  function below1000(n) {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n/10)] + (n%10 ? ' ' + ones[n%10] : '');
    return ones[Math.floor(n/100)] + ' Hundred' + (n%100 ? ' ' + below1000(n%100) : '');
  }
  let result = '';
  if (num >= 1000000) { result += below1000(Math.floor(num/1000000)) + ' Million '; }
  if (num >= 1000) { result += below1000(Math.floor((num%1000000)/1000)) + ' Thousand '; }
  result += below1000(num % 1000);
  result = result.trim() + ' Dollars';
  const centsNum = cents ? parseInt(cents.padEnd(2,'0').slice(0,2), 10) : 0;
  result += centsNum > 0 ? ' and ' + below1000(centsNum) + ' Cents' : ' and Zero Cents';
  return result;
}

// ── Date formatter — converts ISO (2025-07-01) to MM/DD/YYYY ──────────────────
function formatDate(val) {
  if (!val) return '';
  // ISO format: YYYY-MM-DD → MM/DD/YYYY
  const iso = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  return val;
}

// ── Field source definitions — maps each STD 213 field to its source of truth ─
const FIELD_SOURCES = {
  agreementNum:      { label: 'Agreement Number',     from: 'Contract No. / STD 215',     get: (s215, c) => s215?.agreementNum || c?.contractNum || '' },
  purchasingAuthNum: { label: 'Purchasing Auth. No.', from: 'STD 215',                    get: (s215)     => s215?.purchasingAuthNum || '' },
  agencyName:        { label: 'Agency Name',           from: 'User Profile',               get: (s215, c, p) => p?.agencyName || '' },
  contractorName:    { label: 'Contractor Name',       from: 'STD 215',                    get: (s215)     => s215?.contractorName || '' },
  contractorAddress: { label: 'Contractor Address',    from: 'STD 215',                    get: (s215)     => s215?.contractorAddress || '' },
  contractorCity:    { label: 'Contractor City',       from: 'STD 215',                    get: (s215)     => s215?.contractorCity || '' },
  contractorState:   { label: 'Contractor State',      from: 'STD 215',                    get: (s215)     => s215?.contractorState || 'CA' },
  contractorZip:     { label: 'Contractor ZIP',        from: 'STD 215',                    get: (s215)     => s215?.contractorZip || '' },
  startDate:         { label: 'Start Date',            from: 'STD 215',                    get: (s215)     => formatDate(s215?.startDate || '') },
  endDate:           { label: 'End Date',              from: 'STD 215',                    get: (s215)     => formatDate(s215?.endDate || '') },
  maxAmount:         { label: 'Maximum Amount',        from: 'STD 215 / Contract Budget',  get: (s215, c)  => s215?.maxAmount || (c?.budget ? '$' + Number(c.budget).toLocaleString() : '') || '' },
  signatoryName:     { label: 'Agency Signatory Name', from: 'User Profile',               get: (s215, c, p) => p?.signatoryName || '' },
  signatoryTitle:    { label: 'Agency Signatory Title',from: 'User Profile',               get: (s215, c, p) => p?.signatoryTitle || '' },
  agencyAddress:     { label: 'Agency Address',        from: 'User Profile',               get: (s215, c, p) => p?.signatoryAddress || '' },
  agencyCity:        { label: 'Agency City',           from: 'User Profile',               get: (s215, c, p) => p?.signatoryCity || '' },
  agencyZip:         { label: 'Agency ZIP',            from: 'User Profile',               get: (s215, c, p) => p?.signatoryZip || '' },
  dgsExemption:      { label: 'DGS Exemption (Page 2)', from: 'Stage Tab → Exemption Statute (suggested)',  get: (s215, c)  => c?.exemption?.statute || '' },
};

const PENNY_MESSAGES = {
  agreementNum:      (src, cur) => `The Agreement Number you entered (${cur}) doesn't match the contract record (${src}). DGS requires these to be identical across the STD 213 and STD 215. Check the Stage tab to confirm the correct number.`,
  agencyName:        (src, cur) => `Agency name (${cur}) differs from your profile (${src}). If your agency name has changed, update your profile first so all documents stay consistent.`,
  contractorName:    (src, cur) => `Contractor name (${cur}) differs from the STD 215 (${src}). Both forms must use the exact same legal name or DGS/OLS will reject the package.`,
  contractorAddress: (src, cur) => `Contractor address differs from the STD 215. Both forms must have matching contractor information.`,
  startDate:         (src, cur) => `Start date (${cur}) differs from the STD 215 (${src}). Mismatched dates across the STD 213 and STD 215 are a common cause of DGS rejection. Which date is correct?`,
  endDate:           (src, cur) => `End date (${cur}) differs from the STD 215 (${src}). Both forms must have matching agreement terms.`,
  maxAmount:         (src, cur) => `Maximum amount (${cur}) doesn't match the STD 215 or contract budget (${src}). The amount must be identical across all contract documents per SCM requirements.`,
  signatoryName:     (src, cur) => `Signatory name (${cur}) differs from your profile (${src}). Make sure this is the currently authorized signatory — an outdated name can delay DGS approval.`,
  signatoryTitle:    (src, cur) => `Signatory title (${cur}) differs from your profile (${src}). If the title has changed, update your profile to keep all documents consistent.`,
  agencyAddress:     (src, cur) => `Agency address differs from your profile. If your office has relocated, update your profile first.`,
  agencyCity:        (src, cur) => `Agency city differs from your profile. Update your profile to keep all documents consistent.`,
  agencyZip:         (src, cur) => `Agency ZIP differs from your profile. Update your profile to keep all documents consistent.`,
  dgsExemption:      (src, cur) => `The DGS exemption you entered (${cur}) differs from the procurement exemption statute in the Stage tab (${src}). These may be different — the STD 213 exemption box refers specifically to the authority exempting this contract from DGS/OLS review, not the procurement exemption. Confirm which statute applies here.`,
};

const APPROVAL_CLAUSE_MSGS = {
  conflict_no_exemption: () => `You've selected "Energy Safety Approval" but the Stage tab does not show DGS/OLS approval as waived (skipDGS is not checked). Without that flag, DGS/OLS review is required before this contract can take effect. Go to the Stage tab, add a Statutory Exemption, and check "DGS/OLS approval waived" — or change this clause to "DGS/OLS Approval."`,
  conflict_has_exemption: () => `You've selected "DGS/OLS Approval" but the Stage tab shows DGS/OLS approval is waived for this contract. If that exemption is valid, you don't need DGS to approve — using "DGS/OLS Approval" may cause unnecessary routing delays. Confirm the exemption status in the Stage tab.`,
};

function getAutoClause(contract) {
  // skipDGS flag in Stage tab means DGS/OLS approval is waived — Energy Safety can self-approve
  return contract?.exemption?.skipDGS ? 'agency' : 'dgs';
}

// ── Shared style constants ────────────────────────────────────────────────────
const B  = '1px solid #000';
const BL = '1px solid #888';
const LABEL = { fontSize: 7, fontWeight: 700, textTransform: 'uppercase', color: '#111', lineHeight: 1.2, padding: '2px 3px 1px 3px', letterSpacing: '.04em', fontFamily: 'Arial,sans-serif' };
const INPUT = { border: 'none', outline: 'none', background: 'transparent', fontSize: 10, padding: '1px 3px 3px 3px', width: '100%', boxSizing: 'border-box', color: '#000', fontFamily: 'Arial,sans-serif', minHeight: 16 };

function FI({ value, onChange, readOnly, center, bold, large, flagged }) {
  return (
    <input type="text" value={value || ''} onChange={e => !readOnly && onChange && onChange(e.target.value)} readOnly={readOnly}
      style={{ ...INPUT, textAlign: center ? 'center' : 'left', fontWeight: bold ? 700 : 400, fontSize: large ? 13 : 10,
        background: flagged ? '#fff8e1' : 'transparent' }} />
  );
}

// ── Penny Flags Panel ────────────────────────────────────────────────────────
function PennyPanel({ flags, onRevert, onDismiss }) {
  if (!flags || flags.length === 0) return null;
  return (
    <div style={{ marginBottom: 14, borderRadius: 'var(--r-lg)', border: '1.5px solid #c49922', background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>✦</div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#78350f' }}>Penny noticed {flags.length} potential issue{flags.length !== 1 ? 's' : ''}</div>
          <div style={{ fontSize: 10, color: '#92400e' }}>Review before exporting — these may cause DGS routing problems</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {flags.map((flag, i) => (
          <div key={flag.key} style={{ padding: '10px 14px', borderBottom: i < flags.length - 1 ? '1px solid #fde68a' : 'none', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 13, marginTop: 1, flexShrink: 0 }}>⚠</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#78350f', marginBottom: 3 }}>{flag.label}</div>
              <div style={{ fontSize: 11, color: '#92400e', lineHeight: 1.5 }}>{flag.message}</div>
              {flag.sourceValue && (
                <div style={{ marginTop: 4, fontSize: 10, color: '#b45309' }}>
                  Source value: <strong>{flag.sourceValue}</strong> (from {flag.from})
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {flag.sourceValue && (
                <button onClick={() => onRevert(flag.key, flag.sourceValue)}
                  style={{ fontSize: 10, padding: '3px 8px', borderRadius: 5, border: '1px solid #c49922', background: '#fff', color: '#78350f', cursor: 'pointer', fontWeight: 600 }}>
                  ↩ Revert
                </button>
              )}
              <button onClick={() => onDismiss(flag.key)}
                style={{ fontSize: 10, padding: '3px 8px', borderRadius: 5, border: '1px solid #fde68a', background: 'transparent', color: '#92400e', cursor: 'pointer' }}>
                Keep
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Form Page 1 ───────────────────────────────────────────────────────────────
function Page1({ std213, upd, exhibits, generalAttachments, contract, flagged, approvalClause, readOnly }) {
  const f = v => v || '';
  const exhibitRows = (exhibits || []).length > 0 ? exhibits : [
    { label: 'A', name: 'Scope of Work', pages: '', referenceOnly: false, attachments: [] },
    { label: 'B', name: 'Budget Detail and Payment Provisions', pages: '', referenceOnly: false, attachments: [] },
    { label: 'C', name: 'General Terms and Conditions', pages: '', referenceOnly: true, attachments: [] },
  ];

  const clauseText = approvalClause === 'agency'
    ? 'or upon Energy Safety Approval (Whichever is later)'
    : approvalClause === 'dgs'
      ? 'or upon DGS/OLS Approval (Whichever is later)'
      : '';

  return (
    <div style={{ fontFamily: 'Arial,Helvetica,sans-serif', fontSize: 9.5, color: '#000', background: '#fff', border: B }}>

      {/* HEADER */}
      <div style={{ display: 'grid', gridTemplateColumns: '42.8fr 26.9fr 30.3fr', borderBottom: B }}>
        <div style={{ padding: '4px 6px', borderRight: B }}>
          <div style={{ fontSize: 7.5 }}>STATE OF CALIFORNIA - DEPARTMENT OF GENERAL SERVICES</div>
          <div style={{ fontSize: 14, fontWeight: 900, lineHeight: 1.1 }}>STANDARD AGREEMENT</div>
          <div style={{ fontSize: 8, color: '#333' }}>STD 213 (Rev. 04/2020)</div>
        </div>
        <div style={{ borderRight: B }}>
          <div style={{ ...LABEL, textAlign: 'center', borderBottom: BL }}>AGREEMENT NUMBER</div>
          <FI value={std213.agreementNum} onChange={v => upd('agreementNum', v)} readOnly={readOnly} center bold large flagged={flagged.agreementNum} />
        </div>
        <div>
          <div style={{ ...LABEL, textAlign: 'center', borderBottom: BL }}>PURCHASING AUTHORITY NUMBER (If Applicable)</div>
          <FI value={std213.purchasingAuthNum} onChange={v => upd('purchasingAuthNum', v)} readOnly={readOnly} center flagged={flagged.purchasingAuthNum} />
        </div>
      </div>

      {/* SECTION 1 */}
      <div style={{ padding: '3px 6px', borderBottom: B, fontSize: 9 }}>
        <strong>1.</strong> This Agreement is entered into between the Contracting Agency and the Contractor named below:
      </div>
      <div style={{ borderBottom: BL }}>
        <div style={LABEL}>CONTRACTING AGENCY NAME</div>
        <FI value={std213.agencyName} onChange={v => upd('agencyName', v)} readOnly={readOnly} flagged={flagged.agencyName} />
      </div>
      <div style={{ borderBottom: B }}>
        <div style={LABEL}>CONTRACTOR NAME</div>
        <FI value={std213.contractorName} onChange={v => upd('contractorName', v)} readOnly={readOnly} flagged={flagged.contractorName} />
      </div>

      {/* SECTION 2 */}
      <div style={{ padding: '3px 6px', borderBottom: B, fontSize: 9 }}>
        <strong>2.</strong> The term of this Agreement is:
      </div>
      <div style={{ borderBottom: BL }}>
        <div style={LABEL}>START DATE</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '1px 3px 3px 3px' }}>
          <input
            type="text"
            value={formatDate(std213.startDate)}
            onChange={e => !readOnly && upd('startDate', e.target.value)}
            readOnly={readOnly}
            placeholder="MM/DD/YYYY"
            style={{ border: 'none', outline: 'none', background: flagged.startDate ? '#fff8e1' : 'transparent',
              fontSize: 10, width: 88, color: '#000', fontFamily: 'Arial,sans-serif', flexShrink: 0 }}
          />
          {clauseText && <span style={{ fontSize: 8.5, fontStyle: 'italic', color: '#333', whiteSpace: 'nowrap' }}>{clauseText}</span>}
        </div>
      </div>
      <div style={{ borderBottom: B }}>
        <div style={LABEL}>THROUGH END DATE</div>
        <input
          type="text"
          value={formatDate(std213.endDate)}
          onChange={e => !readOnly && upd('endDate', e.target.value)}
          readOnly={readOnly}
          placeholder="MM/DD/YYYY"
          style={{ ...INPUT, background: flagged.endDate ? '#fff8e1' : 'transparent' }}
        />
      </div>

      {/* SECTION 3 */}
      <div style={{ borderBottom: B }}>
        <div style={{ padding: '3px 6px', borderBottom: BL, fontSize: 9 }}><strong>3.</strong> The maximum amount of this Agreement is:</div>
        <div style={{ padding: '3px 6px', background: flagged.maxAmount ? '#fff8e1' : 'transparent' }}>
          <div style={{ fontSize: 13, fontWeight: 700, paddingBottom: 2 }}>{f(std213.maxAmount)}</div>
          <div style={{ fontSize: 8.5, fontStyle: 'italic', color: '#222' }}>{numberToWords(std213.maxAmount)}</div>
          {!readOnly && <input type="text" value={std213.maxAmount || ''} onChange={e => upd('maxAmount', e.target.value)}
            style={{ ...INPUT, fontSize: 13, fontWeight: 700, position: 'absolute', opacity: 0, pointerEvents: 'none' }} />}
        </div>
        {!readOnly && (
          <div style={{ padding: '0 6px 4px' }}>
            <input type="text" value={std213.maxAmount || ''} onChange={e => upd('maxAmount', e.target.value)}
              placeholder="e.g. $150,000.00"
              style={{ ...INPUT, fontSize: 10, background: flagged.maxAmount ? '#fff8e1' : '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 6px' }} />
          </div>
        )}
      </div>

      {/* SECTION 4 */}
      <div style={{ borderBottom: B }}>
        <div style={{ padding: '3px 6px', borderBottom: B, fontSize: 9 }}>
          <strong>4.</strong> The parties agree to comply with the terms and conditions of the following exhibits, which are by this reference made a part of the Agreement.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 80px', borderBottom: BL, background: '#f0f0f0' }}>
          <div style={{ fontSize: 8, fontWeight: 700, padding: '3px 4px', borderRight: B, textAlign: 'center' }}>Exhibit</div>
          <div style={{ fontSize: 8, fontWeight: 700, padding: '3px 4px', borderRight: B, textAlign: 'center' }}>Title</div>
          <div style={{ fontSize: 8, fontWeight: 700, padding: '3px 4px', textAlign: 'center' }}>Pages</div>
        </div>
        {exhibitRows.map((ex, i) => (
          <div key={ex.id || i}>
            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 80px', borderBottom: i < exhibitRows.length - 1 || (ex.attachments||[]).length > 0 ? BL : 'none' }}>
              <div style={{ borderRight: B, padding: '3px 4px', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Exhibit {ex.label}{ex.referenceOnly ? ' *' : ''}
              </div>
              <div style={{ borderRight: B, padding: '3px 4px', fontSize: 9, display: 'flex', alignItems: 'center' }}>{ex.name}</div>
              <div style={{ padding: '3px 4px', fontSize: 9, display: 'flex', alignItems: 'center' }}>{ex.pages}</div>
            </div>
            {(ex.attachments || []).map((att, ai) => (
              <div key={ai} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 80px', borderBottom: ai < ex.attachments.length - 1 ? BL : (i < exhibitRows.length - 1 ? BL : 'none'), background: '#fafafa' }}>
                <div style={{ borderRight: B, padding: '2px 4px 2px 16px', fontSize: 8, color: '#444', fontStyle: 'italic', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Att. {String.fromCharCode(65 + ai)}</div>
                <div style={{ borderRight: B, padding: '2px 4px', fontSize: 8, color: '#444', display: 'flex', alignItems: 'center' }}>{att.name}</div>
                <div />
              </div>
            ))}
          </div>
        ))}
        {(generalAttachments || []).map((att, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 80px', borderBottom: i < generalAttachments.length - 1 ? BL : 'none', background: '#fafafa' }}>
            <div style={{ borderRight: B, padding: '3px 4px', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Attachment {i + 1}</div>
            <div style={{ borderRight: B, padding: '3px 4px', fontSize: 9, display: 'flex', alignItems: 'center' }}>{att.name}</div>
            <div />
          </div>
        ))}
        <div style={{ padding: '2px 4px', borderTop: BL, fontSize: 7.5, fontStyle: 'italic', color: '#222' }}>
          Items shown with an asterisk (*), are hereby incorporated by reference and made part of this agreement as if attached hereto. These documents can be viewed at <span style={{ color: '#0000cc', textDecoration: 'underline' }}>https://www.dgs.ca.gov/OLS/Resources</span>
        </div>
      </div>

      {/* IN WITNESS */}
      <div style={{ padding: '5px 6px', borderBottom: B, fontSize: 9, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '.04em' }}>
        IN WITNESS WHEREOF, THIS AGREEMENT HAS BEEN EXECUTED BY THE PARTIES HERETO.
      </div>

      {/* CONTRACTOR */}
      <div>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', textAlign: 'center', letterSpacing: '.06em', padding: '4px 6px', borderBottom: BL }}>CONTRACTOR</div>
        <div style={{ borderBottom: BL }}>
          <div style={LABEL}>CONTRACTOR NAME (if other than an individual, state whether a corporation, partnership, etc.)</div>
          <FI value={std213.contractorName} onChange={v => upd('contractorName', v)} readOnly={readOnly} flagged={flagged.contractorName} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '62.5fr 25fr 6.25fr 6.25fr', borderBottom: BL }}>
          <div style={{ borderRight: BL }}><div style={LABEL}>CONTRACTOR BUSINESS ADDRESS</div><FI value={std213.contractorAddress} onChange={v => upd('contractorAddress', v)} readOnly={readOnly} flagged={flagged.contractorAddress} /></div>
          <div style={{ borderRight: BL }}><div style={LABEL}>CITY</div><FI value={std213.contractorCity} onChange={v => upd('contractorCity', v)} readOnly={readOnly} flagged={flagged.contractorCity} /></div>
          <div style={{ borderRight: BL }}><div style={LABEL}>ST</div><FI value={std213.contractorState || 'CA'} onChange={v => upd('contractorState', v)} readOnly={readOnly} center /></div>
          <div><div style={LABEL}>ZIP</div><FI value={std213.contractorZip} onChange={v => upd('contractorZip', v)} readOnly={readOnly} center flagged={flagged.contractorZip} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '62.5fr 37.5fr', borderBottom: BL }}>
          <div style={{ borderRight: BL }}><div style={LABEL}>PRINTED NAME OF PERSON SIGNING</div><FI value={std213.contractorRepName} onChange={v => upd('contractorRepName', v)} readOnly={readOnly} /></div>
          <div><div style={LABEL}>TITLE</div><FI value={std213.contractorRepTitle} onChange={v => upd('contractorRepTitle', v)} readOnly={readOnly} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '62.5fr 37.5fr' }}>
          <div style={{ borderRight: BL, minHeight: 40 }}><div style={LABEL}>CONTRACTOR AUTHORIZED SIGNATURE</div></div>
          <div><div style={LABEL}>DATE SIGNED</div><FI value={std213.contractorSignDate} onChange={v => upd('contractorSignDate', v)} readOnly={readOnly} /></div>
        </div>
      </div>
    </div>
  );
}

// ── Form Page 2 ───────────────────────────────────────────────────────────────
function Page2({ std213, upd, contract, flagged, readOnly, onAskPenny }) {
  const f = v => v || '';
  return (
    <div style={{ fontFamily: 'Arial,Helvetica,sans-serif', fontSize: 9.5, color: '#000', background: '#fff', border: B }}>

      {/* HEADER (repeated) */}
      <div style={{ display: 'grid', gridTemplateColumns: '42.8fr 26.9fr 30.3fr', borderBottom: B }}>
        <div style={{ padding: '4px 6px', borderRight: B }}>
          <div style={{ fontSize: 7.5 }}>STATE OF CALIFORNIA - DEPARTMENT OF GENERAL SERVICES</div>
          <div style={{ fontSize: 14, fontWeight: 900, lineHeight: 1.1 }}>STANDARD AGREEMENT</div>
          <div style={{ fontSize: 8, color: '#333' }}>STD 213 (Rev. 04/2020)</div>
        </div>
        <div style={{ borderRight: B }}>
          <div style={{ ...LABEL, textAlign: 'center', borderBottom: BL }}>AGREEMENT NUMBER</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'center', padding: '4px 3px' }}>{f(std213.agreementNum)}</div>
        </div>
        <div>
          <div style={{ ...LABEL, textAlign: 'center', borderBottom: BL }}>PURCHASING AUTHORITY NUMBER (If Applicable)</div>
          <div style={{ padding: '3px', minHeight: 18 }} />
        </div>
      </div>

      {/* STATE OF CALIFORNIA */}
      <div style={{ borderBottom: B }}>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', textAlign: 'center', letterSpacing: '.06em', padding: '4px 6px', borderBottom: BL }}>STATE OF CALIFORNIA</div>
        <div style={{ borderBottom: BL }}>
          <div style={LABEL}>CONTRACTING AGENCY NAME</div>
          <FI value={std213.agencyName} onChange={v => upd('agencyName', v)} readOnly={readOnly} flagged={flagged.agencyName} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '62.5fr 25fr 6.25fr 6.25fr', borderBottom: BL }}>
          <div style={{ borderRight: BL }}><div style={LABEL}>CONTRACTING AGENCY ADDRESS</div><FI value={std213.agencyAddress} onChange={v => upd('agencyAddress', v)} readOnly={readOnly} flagged={flagged.agencyAddress} /></div>
          <div style={{ borderRight: BL }}><div style={LABEL}>CITY</div><FI value={std213.agencyCity} onChange={v => upd('agencyCity', v)} readOnly={readOnly} flagged={flagged.agencyCity} /></div>
          <div style={{ borderRight: BL }}><div style={LABEL}>ST</div><FI value={std213.agencyStateField || 'CA'} onChange={v => upd('agencyStateField', v)} readOnly={readOnly} center /></div>
          <div><div style={LABEL}>ZIP</div><FI value={std213.agencyZip} onChange={v => upd('agencyZip', v)} readOnly={readOnly} center flagged={flagged.agencyZip} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '62.5fr 37.5fr', borderBottom: BL }}>
          <div style={{ borderRight: BL }}><div style={LABEL}>PRINTED NAME OF PERSON SIGNING</div><FI value={std213.signatoryName} onChange={v => upd('signatoryName', v)} readOnly={readOnly} flagged={flagged.signatoryName} /></div>
          <div><div style={LABEL}>TITLE</div><FI value={std213.signatoryTitle} onChange={v => upd('signatoryTitle', v)} readOnly={readOnly} flagged={flagged.signatoryTitle} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '62.5fr 37.5fr' }}>
          <div style={{ borderRight: BL, minHeight: 40 }}><div style={LABEL}>CONTRACTING AGENCY AUTHORIZED SIGNATURE</div></div>
          <div><div style={LABEL}>DATE SIGNED</div><FI value={std213.agencySignDate} onChange={v => upd('agencySignDate', v)} readOnly={readOnly} /></div>
        </div>
      </div>

      {/* DGS APPROVAL */}
      <div style={{ display: 'grid', gridTemplateColumns: '62.5fr 37.5fr', minHeight: 40 }}>
        <div style={{ borderRight: BL }}>
          <div style={LABEL}>CALIFORNIA DEPARTMENT OF GENERAL SERVICES APPROVAL</div>
          <FI value={std213.dgsExemption} onChange={v => upd('dgsExemption', v)} readOnly={readOnly} flagged={flagged.dgsExemption} />
        </div>
        <div>
          <div style={LABEL}>EXEMPTION (If Applicable)</div>
          <FI value={std213.exemptionDetail} onChange={v => upd('exemptionDetail', v)} readOnly={readOnly} />
        </div>
      </div>
      {!readOnly && (
        <div style={{ padding: '6px 4px', borderTop: BL, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onAskPenny}
            style={{ fontSize: 11, padding: '4px 12px', borderRadius: 7, border: 'none', background: 'linear-gradient(135deg,#c49922,#e8c04a)', color: '#0f1f3d', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 13 }}>✦</span> Ask Penny for Exemptions
          </button>
        </div>
      )}

    </div>
  );
}

// ── Exemption Panel ──────────────────────────────────────────────────────────
function ExemptionPanel({ contract, s215, profile, onSelect, onClose, apiKey, callAI }) {
  const [loading, setLoading] = useState(true);
  const [exemptions, setExemptions] = useState([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);

  const contractContext = `
Contract Type: ${contract?.contractType || 'Standard Agreement (STD 213)'}
IT or Non-IT: ${contract?.isIT ? 'IT (SCM Vol. 2)' : 'Non-IT (SCM Vol. 1)'}
Procurement Method: ${contract?.procMethod || s215?.procMethod || 'Unknown'}
Contract Amount: ${s215?.maxAmount || contract?.budget ? '$' + Number(String(s215?.maxAmount || contract?.budget || '0').replace(/[$,]/g,'')).toLocaleString() : 'Not specified'}
Competitive: ${contract?.competitiveBid ? 'Yes' : contract?.soleSource ? 'No — Sole Source' : 'Not specified'}
Agency: ${profile?.agencyName || 'California State Agency'}
Description: ${contract?.description || contract?.title || 'Not specified'}
Existing statutory exemption in Stage tab: ${contract?.exemption?.active ? (contract.exemption?.statute || 'Active, no statute entered') : 'None'}
DGS waived in Stage tab: ${contract?.exemption?.skipDGS ? 'Yes' : 'No'}
`.trim();

  const systemPrompt = `You are Penny, a California state procurement compliance expert built into GoldenScope.

The analyst is trying to determine if this contract qualifies for an exemption from DGS/OLS review and approval on the STD 213 Standard Agreement.

DGS/OLS approval exemptions are DIFFERENT from procurement exemptions. They specifically determine whether the Department of General Services must review and approve the contract instrument before execution.

Analyze the contract details provided and identify which DGS/OLS approval exemptions may apply, citing exact SCM sections, Public Contract Code, or Government Code provisions.

Common DGS/OLS approval exemption authorities include (but are not limited to):
- SCM Vol. 1, Sec. 4.04 - Contracts not requiring DGS approval (small purchases, interagency, etc.)
- SCM Vol. 2 equivalents for IT contracts
- Public Contract Code §10340 - Competitive bid exemptions
- Government Code §19130 - Personal services exemptions  
- Specific agency statutory authority grants

Return ONLY valid JSON (no markdown, no explanation outside JSON) in this exact structure:
{
  "exemptions": [
    {
      "citation": "SCM Vol. 1, Sec. 4.04 A2",
      "title": "Short descriptive title",
      "reason": "1-2 sentence explanation of why this contract qualifies",
      "caveat": "Any conditions, requirements, or caveats the analyst should know (or empty string)",
      "confidence": "high" or "medium" or "low"
    }
  ],
  "notes": "1-2 sentence overall assessment of DGS approval requirements for this contract"
}

If no exemptions clearly apply, return an empty exemptions array and explain in notes.
Be specific. Reference exact contract details in your reasoning. Cite correctly.`;

  async function fetchExemptions() {
    if (!apiKey || !callAI) {
      setError('AI service unavailable. Please reload the page.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const text = await callAI(apiKey, {
        model: 'claude-sonnet-4-6',
        max_tokens: 1000,
        system: systemPrompt,
        messages: [{ role: 'user', content: `Please analyze this contract and identify applicable DGS/OLS approval exemptions:

${contractContext}` }],
        tag: 'dgs_exemption_analysis',
        contract_id: contract?.id || null,
      });
      const clean = (text || '').replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);
      setExemptions(parsed.exemptions || []);
      setNotes(parsed.notes || '');
    } catch (e) {
      setError('Could not load exemption suggestions. Please try again.');
      console.error('Exemption fetch error:', e);
    }
    setLoading(false);
  }

  // Trigger fetch on mount via ref guard
  const fetchedRef = useState(false);
  if (!fetchedRef[0]) { fetchedRef[1](true); fetchExemptions(); }

  const confidenceColor = c => c === 'high' ? '#16a34a' : c === 'medium' ? '#d97706' : '#94a3b8';
  const confidenceBg = c => c === 'high' ? '#f0fdf4' : c === 'medium' ? '#fffbeb' : '#f8fafc';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.25)' }}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>✦</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#0f1f3d' }}>Penny — DGS Exemption Analysis</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>Identifying applicable exemptions from DGS/OLS review</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>✦</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Analyzing contract details...</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Checking SCM Vol. 1, Vol. 2, PCC, and Government Code</div>
            </div>
          ) : error ? (
            <div style={{ padding: 20, background: '#fef2f2', borderRadius: 10, color: '#dc2626', fontSize: 13 }}>
              {error}
              <button onClick={fetchExemptions} style={{ marginLeft: 10, background: 'none', border: '1px solid #dc2626', borderRadius: 6, padding: '3px 10px', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}>Retry</button>
            </div>
          ) : (
            <>
              {notes && (
                <div style={{ padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, marginBottom: 16, fontSize: 12, color: '#0369a1', lineHeight: 1.6 }}>
                  {notes}
                </div>
              )}
              {exemptions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No clear exemptions identified</div>
                  <div style={{ fontSize: 12 }}>DGS/OLS approval is likely required for this contract.</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {exemptions.map((ex, i) => (
                    <div key={i} style={{ padding: '14px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: confidenceBg(ex.confidence) }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f1f3d', fontFamily: 'monospace' }}>{ex.citation}</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginTop: 2 }}>{ex.title}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: confidenceBg(ex.confidence), border: '1px solid', borderColor: confidenceColor(ex.confidence), color: confidenceColor(ex.confidence) }}>
                            {ex.confidence} confidence
                          </span>
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: ex.caveat ? 8 : 0 }}>{ex.reason}</div>
                      {ex.caveat && (
                        <div style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', padding: '5px 10px', borderRadius: 6, marginBottom: 10, lineHeight: 1.5 }}>
                          ⚠ {ex.caveat}
                        </div>
                      )}
                      <button onClick={() => { onSelect(ex.citation); onClose(); }}
                        style={{ fontSize: 12, padding: '5px 14px', borderRadius: 7, border: 'none', background: 'linear-gradient(135deg,#c49922,#e8c04a)', color: '#0f1f3d', fontWeight: 700, cursor: 'pointer' }}>
                        Use This Exemption →
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>Always verify exemption authority with your agency legal counsel before executing.</div>
          <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 12, cursor: 'pointer', color: '#64748b' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function STD213Tab({ contract, updateC, s215, profile, apiKey, callAI }) {
  const std213 = contract.std213 || {};
  const exhibits = contract.exhibits || [];
  const generalAttachments = contract.general_attachments || [];
  const [flags, setFlags] = useState({}); // key → flag object
  const [dismissedFlags, setDismissedFlags] = useState(new Set());
  const [clauseOverride, setClauseOverride] = useState(null); // null | 'agency' | 'dgs'
  const [generating, setGenerating] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [showExemptionPanel, setShowExemptionPanel] = useState(false);
  const pdfPage1Ref = useRef(null);
  const pdfPage2Ref = useRef(null);

  const autoClause = getAutoClause(contract);
  const approvalClause = clauseOverride ?? autoClause;

  // Field updater with conflict detection
  function upd(key, value) {
    // Pass only the changed key — updateC deep-merges std213 correctly
    updateC({ std213: { [key]: value } });

    const source = FIELD_SOURCES[key];
    if (!source) return;
    const sourceVal = source.get(s215, contract, profile);
    if (!sourceVal) return;

    const normalize = v => String(v || '').trim().toLowerCase().replace(/[$,\s]/g, '');
    const isDiff = normalize(value) !== normalize(sourceVal) && value !== '';

    if (isDiff && !dismissedFlags.has(key)) {
      const msgFn = PENNY_MESSAGES[key];
      setFlags(prev => ({
        ...prev,
        [key]: { key, label: source.label, from: source.from, sourceValue: sourceVal, message: msgFn ? msgFn(sourceVal, value) : `${source.label} differs from ${source.from}.` }
      }));
    } else {
      setFlags(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  }

  function handleClauseChange(choice) {
    const skipDGS = contract?.exemption?.skipDGS;
    let conflictMsg = null;
    if (choice === 'agency' && !skipDGS) conflictMsg = APPROVAL_CLAUSE_MSGS.conflict_no_exemption();
    if (choice === 'dgs' && skipDGS) conflictMsg = APPROVAL_CLAUSE_MSGS.conflict_has_exemption();

    setClauseOverride(choice);
    if (conflictMsg) {
      setFlags(prev => ({ ...prev, _approvalClause: {
        key: '_approvalClause', label: 'Approval Clause Override', from: 'Stage Tab → Exemption',
        sourceValue: statute || '(no exemption on file)',
        message: conflictMsg,
      }}));
    } else {
      setFlags(prev => { const n = { ...prev }; delete n._approvalClause; return n; });
    }
  }

  function revertFlag(key, sourceValue) {
    if (key === '_approvalClause') { setClauseOverride(null); setFlags(prev => { const n = { ...prev }; delete n._approvalClause; return n; }); return; }
    upd(key, sourceValue);
    setFlags(prev => { const n = { ...prev }; delete n[key]; return n; });
  }

  function dismissFlag(key) {
    setDismissedFlags(prev => new Set([...prev, key]));
    setFlags(prev => { const n = { ...prev }; delete n[key]; return n; });
  }

  function applyDefaults() {
    const newData = {};
    Object.entries(FIELD_SOURCES).forEach(([key, src]) => {
      const val = src.get(s215, contract, profile);
      if (val && !std213[key]) newData[key] = val;
    });
    if (!std213.agencyStateField) newData.agencyStateField = 'CA';
    updateC({ std213: { ...std213, ...newData } });
    setFlags({});
    setDismissedFlags(new Set());
  }

  async function handleDownloadPDF() {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
    const h2c = window.html2canvas;
    const jsPDFLib = window.jspdf?.jsPDF || window.jsPDF;
    if (!h2c || !jsPDFLib) { alert('PDF library not loaded. Please refresh and try again.'); return; }
    setGenerating(true);
    const clones = [];
    try {
      const pdf = new jsPDFLib({ unit: 'pt', format: 'letter', orientation: 'portrait' });
      for (const [i, ref] of [[0, pdfPage1Ref], [1, pdfPage2Ref]]) {
        ref.current.style.display = 'block';
        const clone = ref.current.cloneNode(true);
        clone.style.cssText = 'position:fixed;top:-9999px;left:-9999px;z-index:-1;display:block;';
        document.body.appendChild(clone);
        clones.push(clone);
        clone.querySelectorAll('input,textarea').forEach(el => {
          const div = document.createElement('div');
          div.textContent = el.value || '';
          div.style.cssText = `font-size:${window.getComputedStyle(el).fontSize};font-family:Arial,sans-serif;color:#000;padding:2px 3px;line-height:1.4;width:100%;box-sizing:border-box;background:transparent;white-space:normal;word-break:break-word;display:block;min-height:14px;`;
          if (el.parentNode?.style) { el.parentNode.style.overflow='visible'; el.parentNode.style.height='auto'; }
          el.parentNode.replaceChild(div, el);
        });
        clone.querySelectorAll('*').forEach(el => {
          if (el.classList?.contains('pdf-capture-page')) return;
          const cs = window.getComputedStyle(el);
          if (cs.overflow==='hidden'||cs.overflowY==='hidden') el.style.overflow='visible';
          if (cs.height&&cs.height!=='auto'&&parseInt(cs.height)>15&&parseInt(cs.height)<200){el.style.height='auto';el.style.minHeight=cs.height;}
        });
        const canvas = await h2c(clone.querySelector('.pdf-capture-page'), { scale: 2, useCORS: true, backgroundColor: '#ffffff', width: PAGE_W, windowWidth: PAGE_W });
        const imgData = canvas.toDataURL('image/jpeg', 0.97);
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, 612, 792);
        ref.current.style.display = 'none';
      }
      pdf.save((std213.agreementNum || contract.contractNum || 'STD213') + '_Standard_Agreement.pdf');
    } catch (err) {
      console.error('STD 213 PDF error:', err);
      alert('PDF generation failed: ' + err.message);
    } finally {
      clones.forEach(c => document.body.removeChild(c));
      setGenerating(false);
    }
  }

  const activeFlags = Object.values(flags);
  const flaggedKeys = Object.fromEntries(activeFlags.map(f => [f.key, true]));

  const pageStyle = { width: PAGE_W, minHeight: Math.round(PAGE_W * 11 / 8.5), background: 'white', padding: '18px 22px', boxSizing: 'border-box', overflow: 'visible' };

  const formProps = { std213, upd, exhibits, generalAttachments, contract, flagged: flaggedKeys };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#1e3a8a', color: '#fff' }}>🏛 CALIFORNIA STATE</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--navy-900)' }}>Standard Agreement — STD 213</span>
          <span style={{ fontSize: 10, color: 'var(--slate-400)' }}>Rev. 04/2020</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={applyDefaults}>↓ Auto-fill from Sources</button>
          <button className="btn btn-primary btn-sm" onClick={handleDownloadPDF} disabled={generating} style={{ minWidth: 130 }}>
            {generating ? '⏳ Generating...' : '⬇ Download PDF'}
          </button>
        </div>
      </div>

      {/* ── Signed / Completed Document Upload ─────────────────── */}
      <div style={{ marginBottom: 16, padding: '16px 20px', borderRadius: 'var(--r-lg)', border: '1.5px solid ' + (std213.signedDoc ? 'var(--green-300)' : 'var(--slate-200)'), background: std213.signedDoc ? 'var(--green-50)' : 'var(--slate-50)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: std213.signedDoc ? 10 : 0 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: std213.signedDoc ? 'var(--green-700)' : 'var(--navy-700)' }}>
              {std213.signedDoc ? '✅ Signed STD 213 on file' : '📄 Upload Signed / Completed STD 213'}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--slate-400)', marginTop: 2 }}>
              Upload the fully executed Standard Agreement after signatures — available for export in the Assembly tab
            </div>
          </div>
          {std213.signedDoc && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={async () => {
                const doc = std213.signedDoc;
                if (doc.storagePath || doc.base64) {
                  let blob;
                  if (doc.storagePath) {
                    const supabase = (await import('../../utils/supabaseClient.js')).default;
                    const { data } = await supabase.storage.from('exhibit-files').download(doc.storagePath);
                    blob = data;
                  } else {
                    const bytes = atob(doc.base64); const arr = new Uint8Array(bytes.length);
                    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
                    blob = new Blob([arr], { type: 'application/pdf' });
                  }
                  if (blob) { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = doc.fileName || 'STD213_Signed.pdf'; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url); }
                }
              }}>↓ Download</button>
              <button className="btn btn-ghost btn-sm" onClick={() => document.getElementById('std213-signed-upload').click()}>↑ Replace</button>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red-400)' }} onClick={() => upd('signedDoc', null)}>✕ Remove</button>
            </div>
          )}
        </div>
        {std213.signedDoc && (
          <div style={{ fontSize: 12, color: 'var(--green-600)' }}>
            {std213.signedDoc.fileName} · Uploaded {std213.signedDoc.uploadedAt ? new Date(std213.signedDoc.uploadedAt).toLocaleDateString() : ''}
          </div>
        )}
        {!std213.signedDoc && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: '1.5px dashed var(--slate-300)', borderRadius: 'var(--r-md)', cursor: 'pointer', background: '#fff', marginTop: 10 }}>
            <span style={{ fontSize: 22 }}>📄</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate-700)' }}>Click to upload signed STD 213</div>
              <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 2 }}>PDF only — fully executed Standard Agreement with all signatures</div>
            </div>
            <input id="std213-signed-upload" type="file" accept=".pdf" style={{ display: 'none' }}
              onChange={async e => {
                const file = e.target.files[0]; if (!file) return;
                const reader = new FileReader();
                reader.onload = async () => {
                  const base64 = reader.result.split(',')[1];
                  let storagePath = null;
                  try {
                    const supabase = (await import('../../utils/supabaseClient.js')).default;
                    const { data: { user } } = await supabase.auth.getUser();
                    if (user && contract?.id) {
                      const path = user.id + '/' + contract.id + '/std213_signed_' + file.name.replace(/\s+/g, '_');
                      const bytes = atob(base64); const arr = new Uint8Array(bytes.length);
                      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
                      const { error } = await supabase.storage.from('exhibit-files').upload(path, new Blob([arr], { type: 'application/pdf' }), { upsert: true });
                      if (!error) storagePath = path;
                    }
                  } catch (e) { console.warn('STD 213 signed upload error:', e.message); }
                  upd('signedDoc', { fileName: file.name, size: file.size, storagePath, base64: storagePath ? null : base64, uploadedAt: new Date().toISOString() });
                };
                reader.readAsDataURL(file);
              }} />
          </label>
        )}
        <input id="std213-signed-upload" type="file" accept=".pdf" style={{ display: 'none' }}
          onChange={async e => {
            const file = e.target.files[0]; if (!file) return;
            const reader = new FileReader();
            reader.onload = async () => {
              const base64 = reader.result.split(',')[1];
              let storagePath = null;
              try {
                const supabase = (await import('../../utils/supabaseClient.js')).default;
                const { data: { user } } = await supabase.auth.getUser();
                if (user && contract?.id) {
                  const path = user.id + '/' + contract.id + '/std213_signed_' + file.name.replace(/\s+/g, '_');
                  const bytes = atob(base64); const arr = new Uint8Array(bytes.length);
                  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
                  const { error } = await supabase.storage.from('exhibit-files').upload(path, new Blob([arr], { type: 'application/pdf' }), { upsert: true });
                  if (!error) storagePath = path;
                }
              } catch (e) { console.warn('STD 213 signed upload error:', e.message); }
              upd('signedDoc', { fileName: file.name, size: file.size, storagePath, base64: storagePath ? null : base64, uploadedAt: new Date().toISOString() });
            };
            reader.readAsDataURL(file);
          }} />
      </div>

      {/* Approval clause selector */}
      <div style={{ padding: '10px 14px', background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: 'var(--r-md)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af' }}>Section 2 Approval Clause</div>
        <div style={{ fontSize: 11, color: '#3b82f6' }}>
          Auto: <strong>{autoClause === 'agency' ? 'Energy Safety Approval' : 'DGS/OLS Approval'}</strong>
          <span style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8' }}>({autoClause === 'agency' ? 'DGS waived in Stage tab' : 'No DGS waiver in Stage tab'})</span>
          {clauseOverride && <span style={{ marginLeft: 6, color: '#f59e0b', fontWeight: 600 }}>⚠ Overridden</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <button onClick={() => { setClauseOverride(null); setFlags(prev => { const n={...prev}; delete n._approvalClause; return n; }); }}
            style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid', borderColor: clauseOverride === null ? '#1e40af' : '#e2e8f0', background: clauseOverride === null ? '#eff6ff' : '#fff', color: clauseOverride === null ? '#1e40af' : '#64748b', cursor: 'pointer', fontWeight: clauseOverride === null ? 700 : 400 }}>Auto</button>
          <button onClick={() => handleClauseChange('agency')}
            style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid', borderColor: clauseOverride === 'agency' ? '#1e40af' : '#e2e8f0', background: clauseOverride === 'agency' ? '#eff6ff' : '#fff', color: clauseOverride === 'agency' ? '#1e40af' : '#64748b', cursor: 'pointer', fontWeight: clauseOverride === 'agency' ? 700 : 400 }}>Energy Safety</button>
          <button onClick={() => handleClauseChange('dgs')}
            style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid', borderColor: clauseOverride === 'dgs' ? '#1e40af' : '#e2e8f0', background: clauseOverride === 'dgs' ? '#eff6ff' : '#fff', color: clauseOverride === 'dgs' ? '#1e40af' : '#64748b', cursor: 'pointer', fontWeight: clauseOverride === 'dgs' ? 700 : 400 }}>DGS/OLS</button>
        </div>
      </div>

      {/* Penny flags panel */}
      <PennyPanel flags={activeFlags} onRevert={revertFlag} onDismiss={dismissFlag} />

      {/* Assembly notice */}
      <div style={{ padding: '7px 12px', background: 'var(--teal-50)', border: '1px solid var(--teal-200)', borderRadius: 'var(--r-md)', fontSize: 11.5, color: 'var(--teal-700)', marginBottom: 14 }}>
        ✦ Exhibits auto-populate from the <strong>Assembly tab</strong>. Fields highlighted in <span style={{ background: '#fff8e1', padding: '0 4px', borderRadius: 3 }}>amber</span> have been overridden from their source.
      </div>

      {/* Page 1 label */}
      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>Page 1 of 2</div>
      <div style={{ marginBottom: 20 }}>
        <Page1 {...formProps} approvalClause={approvalClause} readOnly={false} />
      </div>

      {/* Page 2 label */}
      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>Page 2 of 2</div>
      <Page2 std213={std213} upd={upd} contract={contract} flagged={flaggedKeys} readOnly={false} onAskPenny={() => setShowExemptionPanel(true)} />

      {/* Exemption Panel */}
      {showExemptionPanel && (
        <ExemptionPanel
          contract={contract}
          s215={s215}
          profile={profile}
          apiKey={apiKey}
          callAI={callAI}
          onSelect={citation => upd('dgsExemption', citation)}
          onClose={() => setShowExemptionPanel(false)}
        />
      )}

      {/* Hidden PDF page 1 */}
      <div ref={pdfPage1Ref} style={{ display: 'none' }}>
        <div className="pdf-capture-page" style={pageStyle}>
          <Page1 {...formProps} approvalClause={approvalClause} readOnly={true} />
        </div>
      </div>

      {/* Hidden PDF page 2 */}
      <div ref={pdfPage2Ref} style={{ display: 'none' }}>
        <div className="pdf-capture-page" style={pageStyle}>
          <Page2 std213={std213} upd={() => {}} contract={contract} flagged={{}} readOnly={true} />
        </div>
      </div>



      <div style={{ marginTop: 10, fontSize: 10.5, color: 'var(--slate-400)', textAlign: 'center' }}>
        STD 213 (Rev. 04/2020) · California Department of General Services · All data saves automatically
      </div>
    </div>
  );
}
